import { UseQueryOptions } from '@tanstack/react-query';

import { Segment } from 'domains/segments/types';
import useQuery from 'hooks/useQuery';
import { CuredApiPaths, SortDirection } from 'models/enums';
import { Folder } from 'models/types';

import QueryKeys from './keys';

export type PaginatedSegmentsFolderResponse = {
    total: number;
    offset: number;
    limit: number;
    data: (Segment | Folder)[];
};

type UsePaginatedSegmentsFolderParams = {
    options?: {
        folderId?: number;
        type?: string;
        searchTerm?: string;
        limit?: number;
        offset?: number;
        sortColumn?: string;
        sortDirection?: SortDirection;
    };
    reactQueryOptions?: UseQueryOptions<PaginatedSegmentsFolderResponse, Error>;
};

const usePaginatedSegmentsFolder = ({ options = {}, reactQueryOptions }: UsePaginatedSegmentsFolderParams = {}) => {
    const { folderId, type, searchTerm, limit, offset, sortColumn, sortDirection } = options;

    const queryKey = QueryKeys.SEGMENTS_PAGINATED(folderId, type, searchTerm, offset, limit, sortColumn, sortDirection);

    const params: Record<string, string | number> = {};
    if (folderId !== undefined && folderId > 0) {
        params.folderId = String(folderId);
    }
    if (type) {
        params.type = type;
    }
    if (searchTerm?.trim()) {
        params.searchTerm = searchTerm.trim();
    }
    if (limit !== undefined) {
        params.limit = limit;
    }
    if (offset !== undefined) {
        params.offset = offset;
    }
    if (sortColumn && sortDirection) {
        params.sort = `${sortColumn}:${sortDirection}`;
    }

    const axiosOptions = Object.keys(params).length > 0 ? { params } : {};

    return useQuery<PaginatedSegmentsFolderResponse>(
        queryKey,
        CuredApiPaths.SEGMENTS_PAGINATED,
        reactQueryOptions,
        axiosOptions
    );
};

export default usePaginatedSegmentsFolder;
